import { addYears, endOfYear } from 'date-fns'
import format from 'date-fns/format'
import objectHash from 'object-hash'
import React, { useEffect, useMemo, useState } from 'react'
import { useInterval } from 'react-use'
import styled from 'styled-components'
import {
  buildCompleteTimeSeriesPreviewSeries,
  buildCompleteTimeSeriesPreviewTime,
} from '~/components/timeSeriesPreview.util'
import useAction from '~/prix/react/hooks/action'
import { parseDate, stringifyDate } from '~/prix/types/date'
import { formatDateTime, parseDateTime } from '~/prix/types/dateTime'
import {
  generateIncidenceDates,
  timeTypeForCurrentOnly,
  timeTypeForIncidenceRealtime,
  timeTypeForIncidenceRealtimePrediction,
  timeTypeForPrevalence,
} from '~/prix/types/presets/time'
import actionDefinitions from '~/packages/actionDefinitions'
import {
  Layer,
  SpaceGeoStandardId,
  TimeIncidence,
  TimePrevalence,
} from '~/packages/general/variable/spaceTime.types'
import { normalizeLayerTimeForVariable } from '~/packages/general/variable/recommendedLayerForVariable.util'
import Modal from '~/prix/react/components/modal'
import Handle from '~/prix/react/components/handle'
import DateField from '~/prix/react/components/form/field/dateTimeField'
import TimeSeriesPreview from '~/components/timeSeriesPreview'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { ReportWithLayer } from '~/components/mapContent/modals/reports/reports.types'
import { VariableAndOptionalResult } from '~/packages/general/variable/variables.typing'
import {
  BaseWrapper,
  ChildrenOptions,
  ChildrenOption,
  Left,
  Title,
  WrapperButtons,
  LastButton,
  ArticleItems,
} from '../styles/modals'
import { ButtonVariants } from '~/components/buttonVariants'

export const WrapperModalTime = styled(BaseWrapper).attrs(() => ({
  $flexDirection: 'column',
  $maxHeight: '80vh',
  $justifyLeftContent: 'space-around',
}))`
  .top {
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    width: 100%;
    padding: ${({ theme }) => theme.padding(2)} ${({ theme }) => theme.padding(2)};

    input {
      border: none;
      border-radius: ${({ theme }) => theme.borderRadius};
      padding: ${({ theme }) => theme.padding(2)} ${({ theme }) => theme.padding(2)};
    }

    .span {
      margin: 0 ${({ theme }) => theme.padding(2)};

      @media (max-width: 575px) {
        display: none;
      }
    }

    .date-input-wrapper {
      display: flex;
      flex-direction: row;
      padding-top: 12px;
      width: fit-content;
      gap: 12px;
      justify-content: center;
      align-items: center;

      .title {
        @media (min-width: 575px) {
          display: none;
        }
        @media (max-width: 575px) {
          margin: 0 3px 2px 0;
        }
      }

      @media (max-width: 575px) {
        flex: 1;
        gap: unset;
        flex-direction: column;
        align-items: flex-start;

        input {
          margin-bottom: 7px;
        }
      }
    }
  }
  .bottom {
    display: flex;
    position: relative;
    width: 100%;
    height: clamp(190px, 28vh, 250px);
    min-height: 190px;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: ${({ theme }) => theme.borderRadius};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 8px 14px 10px;

    @media (max-width: 575px) {
      height: clamp(170px, 25vh, 210px);
      min-height: 170px;
      padding: 8px 10px 10px;
    }
  }

  ${ChildrenOptions} {
    /* min-height: 100px; */
    max-height: 500px;
    width: 100%;

    ${ChildrenOption} {
      &.active,
      &.active:hover {
        background-color: ${({ theme }) => theme.colors.lightGrey};
      }
    }
  }
`

// removed local styled components in favor of shared ones

const AlertWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.padding(2)};
  gap: ${({ theme }) => theme.padding(2)};
`

interface ModalSelectTimeProps {
  layer: Layer
  isOpen: boolean
  onClose: () => void
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  onChangeReport: (report: ReportWithLayer | null) => void
}

export default function ModalSelectTime({
  layer,
  isOpen,
  onClose,
  onChangeLayer,
  onChangeReport,
}: ModalSelectTimeProps) {
  const [now, setNow] = useState(() => formatDateTime(new Date()))
  const [currentLayer, setCurrentLayer] = useState<Layer>(() => normalizeLayerTimeForVariable(layer))
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const maxSelectableDate = useMemo(() => endOfYear(addYears(new Date(), 1)), [])

  useEffect(() => {
    setCurrentLayer(normalizeLayerTimeForVariable(layer))
  }, [layer])

  const previewTimeType = currentLayer.variable.timeType
  const previewIsPredicted =
    previewTimeType === 'incidence' && (currentLayer.time as TimeIncidence).type.endsWith('Predicted')

  const previewTime = useMemo(() => {
    if (previewTimeType !== 'incidence') {
      return null
    }

    return buildCompleteTimeSeriesPreviewTime({
      isPredicted: previewIsPredicted,
    })
  }, [previewIsPredicted, previewTimeType])

  const currentVariable = currentLayer.variable as VariableAndOptionalResult
  const previewActionDefinitionKey = currentVariable.actionDefinitionKey

  const previewActionDefinition = useMemo(() => {
    if (typeof previewActionDefinitionKey !== 'string') {
      throw new Error(`Action definition key not found for variable ${currentVariable.key}`)
    }

    const actionDefinition = actionDefinitions.find(
      actionDefinition => actionDefinition.key === previewActionDefinitionKey,
    )
    if (!actionDefinition) {
      throw new Error(`Action definition not found for variable ${currentLayer.variable.key}`)
    }

    return actionDefinition
  }, [currentVariable.key, previewActionDefinitionKey])

  const previewSpaceDelimitationId = (
    currentLayer.space as {
      delimitationId: SpaceGeoStandardId
    }
  ).delimitationId

  const previewSpace = useMemo(
    () => ({
      delimitationType: currentLayer.space.delimitationType,
      delimitationId: previewSpaceDelimitationId,
    }),
    [currentLayer.space.delimitationType, previewSpaceDelimitationId],
  )

  const previewTimeInput = useMemo(
    () =>
      previewTime ?? {
        ...currentLayer.time,
        groupType: 'daily' as const,
      },
    [currentLayer.time, previewTime],
  )

  const previewInput = useMemo(
    () => ({
      variable: currentLayer.variable,
      filter: (currentLayer.variable as VariableAndOptionalResult).filter ?? undefined,
      highlight: (currentLayer.variable as VariableAndOptionalResult).highlight ?? undefined,
      space: previewSpace,
      time: previewTimeInput,
      analysis:
        previewIsPredicted && currentVariable.timeSeries
          ? {
              timeSeries: currentVariable.timeSeries,
              include: {
                items: true,
                prediction: 'auto' as const,
                alerts: false,
                insights: false,
                components: false,
              },
            }
          : undefined,
    }),
    [currentLayer.variable, currentVariable.timeSeries, previewIsPredicted, previewSpace, previewTimeInput],
  )

  const previewFilterHash = useMemo(
    () =>
      objectHash((currentVariable as VariableAndOptionalResult).filter ?? null, {
        respectType: false,
      }),
    [(currentVariable as VariableAndOptionalResult).filter],
  )

  const previewHighlightHash = useMemo(
    () =>
      objectHash((currentVariable as VariableAndOptionalResult).highlight ?? null, {
        respectType: false,
      }),
    [(currentVariable as VariableAndOptionalResult).highlight],
  )

  // The preview request should only rerun when its semantic payload changes.
  const previewRequestKey = useMemo(
    () =>
      objectHash(
        {
          actionDefinitionKey: previewActionDefinition.key,
          variableKey: currentVariable.key,
          filter: previewFilterHash,
          highlight: previewHighlightHash,
          space: previewSpace,
          time: previewTimeInput,
          prediction: previewIsPredicted,
        },
        {
          respectType: false,
        },
      ),
    [
      currentVariable.key,
      previewActionDefinition.key,
      previewFilterHash,
      previewHighlightHash,
      previewIsPredicted,
      previewSpace,
      previewTimeInput,
    ],
  )

  const { timeType } = currentLayer.variable
  const { time } = currentLayer

  const previewResult = useAction(
    previewActionDefinition,
    previewInput,
    [previewRequestKey],
    {
      autoLoad: isOpen && currentLayer.variable.timeType === 'incidence' && Boolean(previewTime),
      staleWhileRevalidate: true,
    },
  )

  const { previewValues, previewHighlights, previewPredictions, previewDates } = useMemo(() => {
    if (!previewTime || !previewResult.result || !('items' in previewResult.result)) {
      return {
        previewValues: [] as number[],
        previewHighlights: [] as boolean[],
        previewPredictions: [] as boolean[],
        previewDates: [] as string[],
      }
    }

    const previewSeries = buildCompleteTimeSeriesPreviewSeries(previewResult.result.items as any[], {
      endDate: previewTime.endDate!,
    })

    return {
      previewValues: previewSeries.values,
      previewHighlights: previewSeries.highlights,
      previewPredictions: previewSeries.predictions,
      previewDates: previewSeries.dates,
    }
  }, [previewResult.result, previewTime])

  const isPreviewLoading = previewResult.isLoading

  useInterval(
    () => {
      setNow(formatDateTime(new Date()))
    },
    isOpen && timeType === 'currentOnly' ? 100 : null,
  )

  useEffect(() => {
    if (timeType !== 'incidence') {
      setStartDate(null)
      setEndDate(null)
      return
    }

    setStartDate(
      (currentLayer.time as TimeIncidence).startDate
        ? (currentLayer.time as TimeIncidence).startDate!
        : null,
    )
    setEndDate(
      (currentLayer.time as TimeIncidence).endDate
        ? (currentLayer.time as TimeIncidence).endDate!
        : null,
    )

    return
  }, [timeType, currentLayer])

  const dateTimeString = useMemo(() => {
    if (timeType !== 'prevalence') {
      return null
    }
    return (time as TimePrevalence).date
      ? format((time as TimePrevalence).date, 'yyyy-MM-dd')
      : null
  }, [timeType, currentLayer])

  const shape: Record<string, string> = {
    prevalence: timeTypeForPrevalence.keysAndLabels ?? {},
    incidence: {
      ...(timeTypeForIncidenceRealtime.keysAndLabels ?? {}),
      ...(timeTypeForIncidenceRealtimePrediction.keysAndLabels ?? {}),
    },
    currentOnly: timeTypeForCurrentOnly.keysAndLabels ?? {},
  }[timeType]

  const entries = Object.entries(shape)

  const handleSelectChildrenOption = (key: string) => {
    if (currentLayer.time.type === key) return

    if (timeType === 'currentOnly') {
      return
    }
    if (timeType === 'prevalence') {
      setCurrentLayer(oldLayer => {
        const date = new Date()
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            type: key as TimePrevalence['type'],
            title: shape[key] ?? '',
            date,
          },
        }
        return newLayer
      })

      return
    }

    const [newStartDateObj, newEndDateObj] =
      generateIncidenceDates[key as keyof typeof generateIncidenceDates]()

    const [newStartDate, newEndDate] = [
      stringifyDate(newStartDateObj),
      stringifyDate(newEndDateObj),
    ]

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          startDate: newStartDate,
          endDate: newEndDate,
          title: ((shape as unknown as any)?.[key] as string) ?? '',
          type: key as TimeIncidence['type'],
        },
      }
      return newLayer
    })

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handleSelectStartDate = (startDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!startDateString) {
      setStartDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: undefined,
            endDate: (oldLayer.time as TimeIncidence).endDate,
          },
        }
        return newLayer
      })
      return
    }

    setStartDate(startDateString)
    const parsedStartDate = startDateString

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'customDateRange'
              : 'customDateRangePredicted',
          startDate: parsedStartDate,
          endDate: (oldLayer.time as TimeIncidence).endDate,
        },
      }
      return newLayer
    })
  }

  const handleSelectEndDate = (endDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!endDateString) {
      setEndDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: (oldLayer.time as TimeIncidence).startDate,
            endDate: undefined,
          },
        }
        return newLayer
      })
      return
    }

    setEndDate(endDateString)
    const endDate = endDateString
    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            endDate <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            endDate <= stringifyDate(new Date()) ? 'customDateRange' : 'customDateRangePredicted',
          startDate: (oldLayer.time as TimeIncidence).startDate,
          endDate: endDate,
        },
      }
      return newLayer
    })
  }
  const handleSelectDateTime = (dateTimeString: string) => {
    if (timeType !== 'prevalence') {
      console.warn('handleSelectDateTime called with timeType', timeType)
      return
    }

    const date = parseDateTime(dateTimeString)
    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...(oldLayer.time as TimePrevalence),
          date,
        },
      }
      return newLayer
    })
  }

  const handleCancel = () => {
    setCurrentLayer(layer)
    onClose()
  }
  const handleAnalyze = () => {
    onChangeReport({
      layer: currentLayer,
      reportKey: 'timeAnalysis',
      reportPayload: {
        spaceDelimitationType: currentLayer.space.delimitationType,
        spaceDelimitationId: (
          currentLayer.space as {
            delimitationId: SpaceGeoStandardId
          }
        ).delimitationId,
      },
    })
    onClose()
  }
  const handleConfirm = () => {
    onChangeLayer(normalizeLayerTimeForVariable(currentLayer))
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Handle>
        <WrapperModalTime>
          <div className='top'>
            {timeType === 'currentOnly' ? now : null}
            {timeType === 'incidence' ? (
              <div className='date-input-wrapper'>
                <span className='title'>Início</span>
                <DateField
                  placeholder='De'
                  value={startDate}
                  isRequired
                  onDirectChange={date =>
                    handleSelectStartDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  maxDate={maxSelectableDate}
                />
                <span className='span'>até</span>
                <span className='title'>Fim</span>
                <DateField
                  placeholder='Até'
                  value={endDate}
                  isRequired
                  onDirectChange={date =>
                    handleSelectEndDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  maxDate={maxSelectableDate}
                />
              </div>
            ) : null}
            {timeType === 'prevalence' ? (
              // <DateField
              //   placeholder='Data'
              //   value={dateTimeString || ''}
              //   onDirectChange={(date: string) => {
              //     handleSelectDateTime(date)
              //   }}
              //   meta={{
              //     isDisabled: (time as TimePrevalence).type !== 'customDate',
              //     withTime: true,
              //   }}
              // />
              <input
                type='datetime-local'
                placeholder='Data'
                value={dateTimeString || ''}
                onChange={event => handleSelectDateTime(event.target.value)}
                disabled={(time as TimePrevalence).type !== 'customDate'}
              />
            ) : null}
          </div>
          <ChildrenOptions>
            {entries.map(([key, name], index) => {
              return (
                <ArticleItems
                  key={index}
                  as={ChildrenOption}
                  className={`${currentLayer.time.type === key ? 'active' : ''}`}
                  role='button'
                  onClick={isPreviewLoading ? () => {} : () => handleSelectChildrenOption(key)}
                  $isLoading={isPreviewLoading}
                >
                  <Left>
                    <Title>{name}</Title>
                  </Left>
                </ArticleItems>
              )
            })}
          </ChildrenOptions>
          {timeType === 'incidence' &&
            (!startDate || !endDate ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>{`A data ${!startDate ? 'inicial' : 'final'} não pode estar vazia`}</p>
              </AlertWrapper>
            ) : parseDate(startDate) > parseDate(endDate) ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>A data inicial não pode ser maior que a data final</p>
              </AlertWrapper>
            ) : previewValues.length || isPreviewLoading ? (
              <div className='bottom'>
                <TimeSeriesPreview
                  isLoading={isPreviewLoading}
                  values={previewValues}
                  dates={previewDates as string[]}
                  highlights={previewHighlights}
                  predictions={previewPredictions}
                  selectedRange={{
                    startDate,
                    endDate,
                  }}
                />
              </div>
            ) : null)}
          <WrapperButtons>
            <ButtonVariants
              onClick={handleCancel}
              label='Cancelar'
              variant='transparent'
              size='sm'
            />
            {timeType === 'incidence' ? (
              <ButtonVariants
                onClick={handleAnalyze}
                label='Analisar tempo'
                variant='transparent'
                size='sm'
                style={{ height: '30px' }}
              />
            ) : null}
            <LastButton>
              <ButtonVariants
                onClick={handleConfirm}
                label='Confirmar'
                variant='primary'
                size='sm'
                style={{ height: '30px' }}
              />
            </LastButton>
          </WrapperButtons>
        </WrapperModalTime>
      </Handle>
    </Modal>
  )
}
